import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCommunicationBridge } from './CommunicationBridgeProvider';
import { useProjectId } from 'hooks/store/data/projects/useProjectId';
import { useProjectById } from 'hooks/store/data/projects/useProjectById';
import { computeProjectTitle } from 'helpers/projects/computeProjectTitle';
import { useGetProjectQuery } from 'features/entry-form-v2/data/useGetProjectQuery';

function extractProjectIdFromPath(pathname: string): string | null {
	const match = pathname.match(/\/projects\/(\d+)/);
	return match?.[1] ?? null;
}

/**
 * Watches the current project and forwards the project title to the shell
 * via the communication bridge.
 *
 * Uses the Redux store when available (old pages sync project data there),
 * and falls back to extracting the project ID from the URL + React Query
 * so the title also works on pages that bypass Redux (e.g. entry-form-v2).
 *
 * When the user leaves a project (no projectId in store or URL), sends an
 * empty title so the shell clears the navbar heading.
 *
 * Renders nothing visible.
 */
export function TitleSync() {
	const { send, isMountedInIframe } = useCommunicationBridge();
	const { pathname } = useLocation();

	const [reduxProjectId] = useProjectId();
	const reduxProject = useProjectById(reduxProjectId);

	const urlProjectId = extractProjectIdFromPath(pathname);
	const projectId = reduxProjectId || urlProjectId;

	const { data: queryData } = useGetProjectQuery(
		{ projectId: projectId! },
		{ enabled: !!projectId && !reduxProject }
	);

	useEffect(() => {
		if (!isMountedInIframe) return;

		if (reduxProject) {
			send({ type: 'set-title', title: computeProjectTitle(reduxProject) });
		} else if (queryData) {
			send({ type: 'set-title', title: queryData.project.projectName });
		} else if (!projectId) {
			send({ type: 'set-title', title: '' });
		}
	}, [reduxProject, queryData, projectId, isMountedInIframe, send]);

	return null;
}
